import React, {useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';

import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import axios from 'axios';

import s from './BookingDetailsPage.module.scss';

import ExtrasPickupStep from './ExtrasPickupStep';
import ParticipantDetailsStep from './ParticipantDetailsStep';
import ReviewStep from './ReviewStep';
import PaymentForm from './PaymentForm';
import ConfirmationStep from './ConfirmationStep';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FIELD_LABEL_TRANSLATIONS } from '../../assets/translations/translations';
import CartReviewStep from "./CartReviewStep"; // import translation map

const stripePromise = loadStripe('pk_test_51Ss1ob3r3A8gOGlqQXcjF3VXZclnH58a9W2GuUIcXUW0XcgGK2VStlBRozaFOQEvXNtpa2nEikiC8uPGpvqyh2uB00WwaxLPNO');  // ← your publishable key

const steps = [
    '追加オプション',
    '送迎情報と参加者情報',
    '確認',
    'カート確認',
    '支払い情報',
    '予約確定'
];
const REZDY_BASE_URL = process.env.REACT_APP_API_LOCAL_URL;

function BookingDetailsPage() {
    const [cartItems, setCartItems] = useState(() => {
        return JSON.parse(localStorage.getItem("booking_cart") || "[]");
    });
    const [quote, setQuote] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const bookingData = location.state;
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const [bookingResponse, setBookingResponse] = useState(null);
    const [paymentMethodId, setPaymentMethodId] = useState(null);
    const [formData, setFormData] = useState({
        pickup: '',
        extras: {},
        bookingFields: {},
        participants: [],
    });
    const [pickupList, setPickupList] = useState([]);
    const [pickupLoading, setPickupLoading] = useState(false);
    const [pickupError, setPickupError] = useState('');

    const {product, selectedSession} = bookingData || {};


    useEffect(() => {
        window.scrollTo({top: 0, behavior: 'smooth'});
    }, [activeStep]);

    useEffect(() => {
        if (!bookingData) {
            navigate('/');
            return;
        }

        setFormData(prev => ({
            ...prev,
            participants: Array(bookingData.totalParticipants || 0).fill({}),
        }));
    }, [bookingData, navigate]);

    useEffect(() => {
        const fetchPickups = async () => {
            if (!product?.pickupId || product.pickupId === 0) {
                setPickupList([]);
                return;
            }

            setPickupLoading(true);
            setPickupError('');

            try {
                const response = await axios.get(
                    `${REZDY_BASE_URL}util/pickups`,
                    {params: {pickupId: product.pickupId}}
                );

                const pickups = response.data.data?.pickupList?.pickupLocations || [];
                setPickupList(pickups);
            } catch (err) {
                console.error('Pickup fetch error:', err);
                setPickupError('ピックアップ場所の読み込みに失敗しました。');
                setPickupList([]);
            } finally {
                setPickupLoading(false);
            }
        };

        fetchPickups();
    }, [product]);
    // useEffect(() => {
    //     const fetchQuote = async () => {
    //         if (!cartItems.length) return;
    //
    //         try {
    //             const res = await axios.post(
    //                 `${REZDY_BASE_URL}quote`,
    //                 {
    //                     items: cartItems.map(i => ({
    //                         productCode: i.productCode,
    //                         startTimeLocal: i.startTimeLocal,
    //                         quantities: i.quantities,
    //                         extras: i.extras
    //                     }))
    //                 }
    //             );
    //
    //             setQuote(
    //                 res.data.data.quote
    //             );
    //         } catch (err) {
    //             console.error(err);
    //         }
    //     };
    //
    //     fetchQuote();
    // }, [cartItems]);
    const validateCurrentStep = () => {
        const errors = {};
        if (activeStep === 1) {
            product.bookingFields
                ?.filter(f => f.requiredPerBooking)
                .forEach(field => {
                    if (!formData.bookingFields[field.label]?.trim()) {
                        errors[`booking_${field.label}`] = `${field.label} は必須です`;
                    }
                });

            formData.participants.forEach((participant, idx) => {
                product.bookingFields
                    ?.filter(f => f.requiredPerParticipant)
                    .forEach(field => {
                        if (!participant[field.label]?.trim()) {
                            errors[`participant_${idx}_${field.label}`] = `参加者 ${idx + 1}: ${field.label} は必須です`;
                        }
                    });
            });
            if (
                product?.pickupId &&
                product.pickupId !== 0 &&
                !formData.pickup
            ) {
                errors.pickup = 'ピックアップ場所を選択してください';
            }
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const updateField = (path, value) => {
        setFormData(prev => {
            const newFormData = JSON.parse(JSON.stringify(prev));

            const [root, ...rest] = path.split('.');

            if (root === 'bookingFields') {
                const fieldName = rest.join('.'); // join back dots
                newFormData.bookingFields[fieldName] = value;

            } else if (root === 'participants') {
                const idx = parseInt(rest[0]);
                const fieldName = rest.slice(1).join('.');

                if (!newFormData.participants[idx]) {
                    newFormData.participants[idx] = {};
                }

                newFormData.participants[idx][fieldName] = value;

            } else {
                newFormData[root] = value;
            }

            return newFormData;
        });
    };

    const updateExtra = (name, qty) => {
        setFormData(prev => ({
            ...prev,
            extras: {...prev.extras, [name]: Math.max(0, qty)},
        }));
    };

    const handleNext = () => {
        if (activeStep === 1 && !validateCurrentStep()) {
            setError('必須項目をすべて入力してください。');
            return;
        }
        setError('');
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handlePaymentSuccess = (pmId) => {
        // setPaymentMethodId(pmId);
        createRezdyBooking(pmId);
    };
    const buildBookingItem = useCallback(() => ({

        productCode: product.productCode,
        productName: product.name,
        startTimeLocal: selectedSession.startTimeLocal,

        quantities: selectedSession.priceOptions
            .map(option => ({
                priceOptionId: option.id,
                optionLabel: option.label,
                value: bookingData.quantities[option.id] || 0
            }))
            .filter(q => q.value > 0),

        extras: Object.entries(formData.extras)
            .filter(([, quantity]) => quantity > 0)
            .map(([name, quantity]) => ({
                name,
                quantity
            })),

        participants: formData.participants.map(participant => ({
            fields: Object.entries(participant)
                .filter(([, value]) => value)
                .map(([label, value]) => ({
                    label,
                    value: value.toString()
                }))
        })),

        fields: Object.entries(formData.bookingFields)
            .filter(([, value]) => value && value.toString().trim() !== '')
            .map(([label, value]) => ({
                label,
                value: value.toString()
            })),
        pickup: formData.pickup
    }), [
        product,
        selectedSession,
        bookingData,
        formData
    ]);
    const handleAddToCartAndContinue = () => {

        const item = buildDraftBookingItem();

        saveOrUpdateCartItem(item);

        window.dispatchEvent(
            new Event('cartUpdated')
        );

        navigate('/all-tours');
    };
    const handleAddToCartAndReview = () => {

        const item = buildDraftBookingItem();

        saveOrUpdateCartItem(item);

        window.dispatchEvent(
            new Event('cartUpdated')
        );

        navigate('/cart');
    };
    const removeCartItem = (index) => {
        const updated = cartItems.filter(
            (_, i) => i !== index
        );

        setCartItems(updated);

        localStorage.setItem(
            "booking_cart",
            JSON.stringify(updated)
        );
        window.dispatchEvent(
            new Event('cartUpdated')
        );
    };

    const createRezdyBooking = async (pmId) => {
        setLoading(true);
        setError('');

        try {
            const bookingPayload = {
                items: [
                    ...cartItems,
                    buildBookingItem()
                ],
                customer: {
                    firstName: formData.bookingFields['First Name'] || 'ゲスト',
                    lastName: formData.bookingFields['Last Name'] || 'ユーザー',
                    email: formData.bookingFields['Email'] || 'no-email@example.com',
                    phone: formData.bookingFields['Mobile Number'] || '0',
                },
                resellerReference: `MYWEBSITE-${Date.now()}`,
                sendNotifications: true,
                ...(formData.bookingFields.Comments && {comments: formData.bookingFields.Comments}),
                ...(formData.pickup && {
                    pickupLocation: {
                        locationName: formData.pickup,
                        minutesPrior: 15
                    }
                }),
                fields: Object.entries(formData.bookingFields)
                    .filter(([label, value]) => value && value.toString().trim() !== '' && label !== 'Comments')
                    .map(([label, value]) => ({label, value: value.toString()})),
                extras: Object.entries(formData.extras)
                    .filter(([, quantity]) => quantity > 0)
                    .map(([name, quantity]) => ({name, quantity})),
                participants: formData.participants.map(participant => ({
                    fields: Object.entries(participant)
                        .filter(([, value]) => value && value.toString().trim() !== '')
                        .map(([label, value]) => ({label, value: value.toString()}))
                })),
                status:"PENDING_SUPPLIER",
                paymentMethodId:pmId
            };

            const response = await axios.post(
                `${REZDY_BASE_URL}booking`,
                bookingPayload,
                {headers: {'Content-Type': 'application/json'}}
            );

            if (response.data.success) {
                localStorage.removeItem("booking_cart");
                setCartItems([]);
                const order = response.data.data.api_response.booking;
                setBookingResponse(order);
                window.dispatchEvent(
                    new Event('cartUpdated')
                );
                handleNext();
            } else {
                throw new Error(response.data.message || '予約に失敗しました');
            }
        } catch (err) {
            console.error('Booking error:', err.response?.data || err);
            setError(err.response?.data?.message || '予約の作成に失敗しました。');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return <ExtrasPickupStep
                    product={product}
                    formData={formData}
                    updateExtra={updateExtra}
                    updateField={updateField}

                />;
            case 1:
                return <ParticipantDetailsStep
                    product={product}
                    formData={formData}
                    updateField={updateField}
                    validationErrors={validationErrors}
                    pickupList={pickupList}
                    pickupLoading={pickupLoading}
                    pickupError={pickupError}
                />;
            case 2:
                return   <ReviewStep
                    product={product}
                    selectedSession={selectedSession}
                    bookingData={bookingData}
                    formData={formData}
                    error={error}
                    onAddToCart={handleAddToCartAndContinue}
                    onCheckout={handleAddToCartAndReview}
                />;
            case 3:
                return (
                    <CartReviewStep
                        cartItems={cartItems}
                        quote={quote}
                        onRemove={removeCartItem}
                        onCheckout={() => setActiveStep(4)}
                    />
                );
            case 4:
                return (<Elements stripe={stripePromise}>
                    <PaymentForm
                        email={formData.bookingFields['Email'] || 'guest@example.com'}
                        name={`${formData.bookingFields['First Name'] || ''} ${formData.bookingFields['Last Name'] || ''}`}
                        onSuccess={(paymentMethodId) => {

                            // Now call your createRezdyBooking() function
                            handlePaymentSuccess(paymentMethodId);
                        }}
                    />
                </Elements>);
            case 5:
                return <ConfirmationStep bookingResponse={bookingResponse} product={product}
                                         selectedSession={selectedSession} bookingData={bookingData}
                                         navigate={navigate}/>;
            default:
                return null;
        }
    };
    const saveOrUpdateCartItem = (item) => {
        const existingIndex = cartItems.findIndex(
            cartItem =>
                cartItem.productCode === item.productCode &&
                cartItem.startTimeLocal === item.startTimeLocal
        );

        let updated;

        if (existingIndex !== -1) {
            // Update existing item
            updated = [...cartItems];
            updated[existingIndex] = item;
        } else {
            // Add new item
            updated = [...cartItems, item];
        }

        setCartItems(updated);

        localStorage.setItem(
            "booking_cart",
            JSON.stringify(updated)
        );

        window.dispatchEvent(
            new Event("cartUpdated")
        );

        return updated;
    };
    const buildDraftBookingItem = () => ({
        productCode: product.productCode,
        productName: product.name,
        startTimeLocal: selectedSession.startTimeLocal,

        quantities: selectedSession.priceOptions
            .map(option => ({
                priceOptionId: option.id,
                optionLabel: option.label,
                value: bookingData.quantities[option.id] || 0
            }))
            .filter(q => q.value > 0),

        extras: Object.entries(formData.extras)
            .filter(([, quantity]) => quantity > 0)
            .map(([name, quantity]) => ({
                name,
                quantity
            })),

        fields: product.bookingFields || [],

        participants: Array.from(
            { length: bookingData.totalParticipants || 0 },
            () => ({ fields: [] })
        ),

        pickup: '',
        pickupListId: product?.pickupId

    });
    return (
        <div className={s.page}>
            <div className={s.container}>
                <div className={s.header}>
                    <h1 className={s.header__title}>{product?.name}</h1>
                    <p className={s.header__subtitle}>
                        {selectedSession && format(new Date(selectedSession.startTimeLocal), 'yyyy年M月d日 (EEE)')} •{' '}
                        {bookingData?.totalParticipants}名
                    </p>
                </div>

                <div className={s.stepper}>
                    <Stepper activeStep={activeStep}>
                        {steps.map(label => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </div>

                <div className={s.content}>
                    {renderStepContent()}
                </div>

                {activeStep === 0 ? (
                    <div className={"row p-3"}>

                        {/*<Button*/}
                        {/*    variant="outlined"*/}
                        {/*    disabled*/}
                        {/*>*/}
                        {/*    戻る*/}
                        {/*</Button>*/}
                        <div className={"col-12 col-md-6 mb-3"}>
                        <Button
                            className={s.btnBlock}
                            variant="outlined"
                            color="secondary"
                            onClick={handleAddToCartAndContinue}
                        >
                            カートに追加して別のツアーを予約
                        </Button>
                        </div>
                        <div className={"col-12 col-md-6 mb-3"}>
                        <Button
                            className={s.btnBlock}
                            variant="contained"
                            onClick={handleAddToCartAndReview}
                        >
                            チェックアウト
                        </Button>
                        </div>

                    </div>
                ) : (
                    activeStep < steps.length - 1 &&
                    activeStep !== 2 &&
                    activeStep !== 3 && (
                        <div className={s.nav}>
                            <Button
                                variant="outlined"
                                onClick={handleBack}
                            >
                                戻る
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleNext}
                            >
                                次へ
                            </Button>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

export default BookingDetailsPage;